import React, { useState } from 'react';
import { URLS } from 'services/urls';
import { NSButtonBase } from 'bricks/NSButton/NSButton';
import { BOE_PROPERTY_TYPES, BOEDefaultValuesByPropertyType } from 'views/DealDashboard/BOE/components/SlideoutForm/constants';
import { BOESlideoutFormValues } from 'views/DealDashboard/BOE/components/SlideoutForm/types';
import './BOEEstimate.scss';
import BOEEstimator from './BOEEstimator/BOEEstimator';
import { SignUpStage, useSignUpContext } from '../SignUp/SignUpContext';

const assetTypes = [
    { label: 'Apartment', src: URLS.IMAGE_CLOUDFRONT_URL('deal/signup/apartment_building.png'), value: BOE_PROPERTY_TYPES.APARTMENT },
    { label: 'Retail', src: URLS.IMAGE_CLOUDFRONT_URL('deal/signup/retail.png'), value: BOE_PROPERTY_TYPES.RETAIL },
    { label: 'Industrial', src: URLS.IMAGE_CLOUDFRONT_URL('deal/signup/industrial.png'), value: BOE_PROPERTY_TYPES.INDUSTRIAL },
    { label: 'Office', src: URLS.IMAGE_CLOUDFRONT_URL('deal/signup/office.png'), value: BOE_PROPERTY_TYPES.OFFICE },
    { label: 'Data center', src: URLS.IMAGE_CLOUDFRONT_URL('deal/signup/data_center.png'), value: BOE_PROPERTY_TYPES.DATA_CENTER },
];

const BOEEstimate: React.FC = () => {
    const { setCurrentStage, updateStageData, currentStageData } = useSignUpContext();
    const storedStageData = currentStageData as BOESlideoutFormValues;
    const [selectedAsset, setSelectedAsset] = useState<string | null>(storedStageData?.propertyType?.value || null);
    const [boeValues, setBoeValues] = useState<BOESlideoutFormValues>(storedStageData || BOEDefaultValuesByPropertyType[selectedAsset || 'apartment']);

    const handleSelectAssetType = (value: string) => {
        if (selectedAsset === value) {
            setSelectedAsset(null);
            return;
        }

        setSelectedAsset(value);
        if (value === storedStageData?.propertyType.value) {
            setBoeValues(storedStageData);
        } else {
            setBoeValues(BOEDefaultValuesByPropertyType[value]);
        }
    };

    const handleNext = () => {
        updateStageData(boeValues);
        setCurrentStage(SignUpStage.PRO_FORMA_QUICK_START);
    };

    return (
        <div className="BOEEstimate">
            <img
                className="BOEEstimate__corner-logo"
                src={URLS.IMAGE_CLOUDFRONT_URL('deal/signup/NSLogo_Stacked.png')}
                alt="ns-logo"
            />
            <div className="BOEEstimate__left-panel">
                <h1>Start with your asset type.</h1>
                <div className="BOEEstimate__asset-options">
                    {assetTypes.map(({ label, src, value }) => (
                        <div
                            key={label}
                            className={`BOEEstimate__asset-card ${selectedAsset === value ? 'BOEEstimate__asset-card--selected' : ''}`}
                            onClick={() => handleSelectAssetType(value)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    handleSelectAssetType(value);
                                }
                            }}
                        >
                            <img src={src} alt={label} />
                            <span>{label}</span>
                        </div>
                    ))}
                </div>
                <NSButtonBase callback={handleNext} disabled={!selectedAsset} text="Next" className="col-2 btn-primary" />
            </div>

            <div
                className="BOEEstimate__right-panel d-flex justify-content-center align-items-center pb-0"
            >
                <div
                    className="BOEEstimate__right-panel-bg"
                    style={{
                        backgroundImage: `url(${URLS.IMAGE_CLOUDFRONT_URL('deal/signup/building_side_image.png')})`,
                    }}
                />
                <div className="BOEEstimate__right-panel-content flex-column justify-content-center align-items-center">
                    {selectedAsset ? (
                        <BOEEstimator
                            selectedAsset={selectedAsset}
                            boeValues={boeValues}
                            setBoeValues={setBoeValues}
                        />
                    ) : (
                        <div className="BOEEstimate__ad">
                            <p className="BOEEstimate__pitch-text text-center mb-2">
                                <span className="font-weight-bold">Get to market faster </span>
                                with the highest and best use across your deal pipeline.
                            </p>
                            <div className="BOEEstimate__testimonial d-flex align-items-center mb-5">
                                <div className="BOEEstimate__testimonial-photo-wrapper">
                                    <img
                                        className="BOEEstimate__testimonial-photo"
                                        src={URLS.IMAGE_CLOUDFRONT_URL('deal/signup/Chavkin.png')}
                                        alt="Testimonial"
                                    />
                                </div>
                                <blockquote>
                                    <p className="mb-1">
                                        <i className="BOEEstimate__pitch-quote">
                                            I’ve not seen anything out there that does a fraction of what Northspyre does,
                                            and as seamlessly as it works.
                                        </i>
                                    </p>
                                    <footer className="mt-2">
                                        <p className="BOEEstimate__pitch-user-name mb-0 font-weight-bold">Peter Chavkin</p>
                                        <i className="font-weight-lighter">Managing Partner, Biddle BREV</i>
                                    </footer>
                                </blockquote>
                            </div>

                            <div className="BOEEstimate__developers">
                                <p className="text-center">
                                    <span className="font-weight-bold">Real results </span>
                                    from boundary-pushing developers.
                                </p>
                                <div className="BOEEstimate__logos">
                                    {Array.from({ length: 10 }, (_, i) => (
                                        <img
                                            key={i}
                                            src={URLS.IMAGE_CLOUDFRONT_URL(`deal/signup/customer-logos/logo${i + 1}.png`)}
                                            alt={`Customer Logo ${i + 1}`}
                                            className="BOEEstimate__logo"
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BOEEstimate;
